import { RefObject, useCallback, useEffect, useRef } from 'react'
import { startsInHorizontalScroller } from '../helpers/gestures'

/**
 * How far in from the pane's left edge a swipe has to start. iOS reserves a strip of
 * about this width for the same gesture; wider than this and it starts claiming drags
 * that began on the avatar column down the left of a timeline.
 */
const EDGE_ZONE_PX = 28

/** Travel before the gesture is claimed from the scroller underneath it. */
const ENGAGE_PX = 10

/** Past this much of the pane's width, letting go goes back rather than springing back. */
const COMMIT_FRACTION = 0.35

/** …or a flick, however short it was. Pixels per millisecond, over the last move. */
const COMMIT_VELOCITY = 0.5

/** Long enough to read as the pane travelling, short enough not to sit in the way. */
const SETTLE_MS = 240

/**
 * Set on the element while a pane is moving, and read by the stylesheet — `dragging`
 * while it tracks the finger, `settling` while it finishes on its own. The two are
 * distinct because a transition is right for exactly one of them: during the drag the
 * transform has to be where the finger is, and any easing there reads as lag.
 */
export const SWIPE_BACK_ATTRIBUTE = 'data-swipe-back'

/** How far through the gesture the pane is, 0–1. The stylesheet's transforms are built on it. */
export const SWIPE_BACK_PROGRESS_PROPERTY = '--swipe-back-progress'

const prefersReducedMotion = (): boolean =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

interface IOptions {
  /**
   * False leaves the element alone — no listeners, no attributes. Pass a
   * touch/coarse-pointer check together with "there is somewhere to go back to":
   * this is about the absence of a platform back gesture, which belongs to the
   * input device and the layout, not to the viewport width alone.
   */
  isEnabled: boolean
  /** Where the gesture lands. Read from a ref, so it may change between gestures. */
  onBack: () => void
}

interface IResult {
  /** Attach to the element holding both panes — the one the transforms are written against. */
  ref: RefObject<HTMLDivElement | null>
  /**
   * The same exit the gesture ends with, for the header's back button — so leaving a
   * conversation looks the same however you asked for it. Falls straight through to
   * `onBack` when the gesture is off, or when the reader has asked for less motion.
   */
  goBack: () => void
}

/**
 * Swipe in from the left edge to go back, the way every phone app does.
 *
 * The app is `display: standalone`, so neither platform offers a back gesture of its
 * own inside it — and below md the conversation list and the conversation share one
 * column, so the only way out of a conversation was a 44px arrow in the header's
 * top-left corner. That corner is the hardest thing on the screen to reach with the
 * thumb of the hand holding the phone, and a list that can only be returned to by
 * aiming at it is most of what makes this surface read as a web page.
 *
 * The gesture publishes its progress as a custom property rather than as React state:
 * the panes are transformed by the stylesheet, which can then move both of them (the
 * conversation travelling, the list behind it catching up) off one number, and a
 * finger's worth of moves never re-renders the surface.
 *
 * Touch events rather than the pointer events `useLongPress` uses, because this one
 * has to take the gesture away from a scroller: `preventDefault` on a pointer event
 * does not stop a scroll, and the `touch-action` that would is a blunt instrument —
 * it cannot tell an edge swipe from a horizontal drag inside the conversation.
 */
export const useEdgeSwipeBack = ({ isEnabled, onBack }: IOptions): IResult => {
  const ref = useRef<HTMLDivElement>(null)
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Held in a ref so a caller's inline callback doesn't re-run the effect below,
  // which would tear down and re-add the listeners on every render of the surface.
  const onBackRef = useRef(onBack)
  useEffect(() => {
    onBackRef.current = onBack
  }, [onBack])

  /**
   * Takes the element rather than reading the ref, and every caller has it in scope
   * already. React detaches a ref before the effect cleanup that would use it runs, so
   * reading `ref.current` here left a pane that unmounted mid-gesture still carrying the
   * dragging attribute and a transform — on an element that outlives this surface.
   */
  const clearPaneState = useCallback((element: HTMLElement | null) => {
    if (settleTimer.current) {
      clearTimeout(settleTimer.current)
      settleTimer.current = null
    }
    if (!element) return
    element.removeAttribute(SWIPE_BACK_ATTRIBUTE)
    element.style.removeProperty(SWIPE_BACK_PROGRESS_PROPERTY)
  }, [])

  /** Runs the pane the rest of the way — out to the right, or back where it was. */
  const settle = useCallback(
    (destination: 'back' | 'stay') => {
      const element = ref.current
      if (!element) return

      if (prefersReducedMotion()) {
        // `onBack` first, so the pane is never left untransformed while the layout
        // still says a conversation is open — that order is what keeps the commit
        // from flashing the conversation back into view on its way out.
        if (destination === 'back') onBackRef.current()
        clearPaneState(element)
        return
      }

      element.setAttribute(SWIPE_BACK_ATTRIBUTE, 'settling')
      // Next frame, so the transition has the pane's current position as its start
      // value. Set in the same frame the attribute lands, the browser sees a single
      // style change and jumps to the end instead of animating.
      requestAnimationFrame(() => {
        element.style.setProperty(SWIPE_BACK_PROGRESS_PROPERTY, destination === 'back' ? '1' : '0')
      })

      settleTimer.current = setTimeout(() => {
        if (destination === 'back') onBackRef.current()
        clearPaneState(element)
      }, SETTLE_MS)
    },
    [clearPaneState],
  )

  useEffect(() => {
    const element = ref.current
    if (!isEnabled || !element) return

    let origin: { x: number; y: number } | null = null
    let isEngaged = false
    let paneWidth = 0
    let lastX = 0
    let lastAt = 0
    let velocity = 0

    const forget = () => {
      origin = null
      isEngaged = false
    }

    const handleTouchStart = (event: TouchEvent) => {
      // A touch keeps targeting the node it began on, so if that node leaves the DOM
      // mid-gesture its touchend never bubbles here. Left engaged, the old gesture would
      // cancel every move on this element — freezing the page until the route changed.
      if (isEngaged) clearPaneState(element)
      forget()

      // More than one finger is a pinch or a two-finger scroll, never this.
      if (event.touches.length !== 1) return
      const touch = event.touches[0]
      const bounds = element.getBoundingClientRect()
      if (touch.clientX - bounds.left > EDGE_ZONE_PX) return
      if (startsInHorizontalScroller(event.target, element)) return

      origin = { x: touch.clientX, y: touch.clientY }
      paneWidth = bounds.width
      lastX = touch.clientX
      lastAt = event.timeStamp
      velocity = 0
    }

    const handleTouchMove = (event: TouchEvent) => {
      if (!origin || paneWidth === 0) return
      const touch = event.touches[0]
      const dx = touch.clientX - origin.x
      const dy = touch.clientY - origin.y

      if (!isEngaged) {
        // A back swipe and a scroll of the timeline start identically. Whichever axis
        // clears the threshold first owns the gesture and the loser is out for the rest
        // of it — so a scroll that drifts sideways can never turn into a navigation
        // halfway down the conversation.
        if (Math.abs(dy) > ENGAGE_PX && Math.abs(dy) >= Math.abs(dx)) {
          forget()
          return
        }
        if (dx < ENGAGE_PX) return
        isEngaged = true
        element.setAttribute(SWIPE_BACK_ATTRIBUTE, 'dragging')
      }

      // The scroller underneath keeps the gesture otherwise, and the pane travels while
      // the timeline scrolls behind it. This is the whole reason for touch events here.
      event.preventDefault()

      // And nothing above us gets a say. Two of these can be nested — the app shell wraps
      // every page, and Messages puts one on its own two-pane row — and both would answer
      // the same swipe, navigating twice. The inner one sees the event first and claims
      // it, which is the right way round: it knows what "back" means on its own surface.
      event.stopPropagation()

      const elapsed = event.timeStamp - lastAt
      if (elapsed > 0) velocity = (touch.clientX - lastX) / elapsed
      lastX = touch.clientX
      lastAt = event.timeStamp

      // Clamped rather than rubber-banded: there is nothing to the right of a
      // conversation to reveal, and nothing past the list to the left of it.
      const progress = Math.min(1, Math.max(0, dx / paneWidth))
      element.style.setProperty(SWIPE_BACK_PROGRESS_PROPERTY, `${progress}`)
    }

    const handleTouchEnd = () => {
      if (!isEngaged || !origin) {
        forget()
        return
      }
      const progress = (lastX - origin.x) / paneWidth
      forget()
      settle(progress > COMMIT_FRACTION || velocity > COMMIT_VELOCITY ? 'back' : 'stay')
    }

    element.addEventListener('touchstart', handleTouchStart, { passive: true })
    element.addEventListener('touchmove', handleTouchMove, { passive: false })
    element.addEventListener('touchend', handleTouchEnd)
    element.addEventListener('touchcancel', handleTouchEnd)

    return () => {
      element.removeEventListener('touchstart', handleTouchStart)
      element.removeEventListener('touchmove', handleTouchMove)
      element.removeEventListener('touchend', handleTouchEnd)
      element.removeEventListener('touchcancel', handleTouchEnd)
      forget()
      clearPaneState(element)
    }
  }, [isEnabled, settle, clearPaneState])

  const goBack = useCallback(() => {
    if (!isEnabled) {
      onBackRef.current()
      return
    }
    settle('back')
  }, [isEnabled, settle])

  return { ref, goBack }
}

export default useEdgeSwipeBack
